// Store → viewport commands. Every send carries the whole of its slice (all layer modes, the whole
// isolation), so the viewport can't drift from the app. After a load, everything is sent again.
// Sims, their layers and overlays are diffed against what was last sent, so a scene switch unloads
// exactly the slots and overlays it no longer has. A viewport that speaks an older API than a command
// or a sim format needs (its `ready.api`, or the formats its `ready.sims` lists) isn't sent it, and
// neither is a run with no placement in the loaded pack's space: the app reads the sims itself, so
// traces and Methods still work. A surface run (an fsaverage seizure) is placed on the HD cortex the
// viewport draws over the pack (BigBrain's, data/brain.ts) when that cortex has a map from its mesh;
// the activity it draws there (the cortex and the deep structures, by parcel) then replaces the run's
// overlay. A sim the viewport refuses anyway is noted per slot: its overlays stand in, or the HUD says
// it isn't drawn. A view on its own pack (a tissue pack) loads that pack instead of the scene's; if the
// viewport can't load it, the scene goes back to its own pack and the HUD says why.

import type { Command, Mode, Viewport } from './api.ts';
import { on } from './events.ts';
import { NO_PARTICLES, useAtlas, type AtlasState, type SimSlot } from '../app/store.ts';
import { DEFAULT_MODES, loadCatalogue, isContext } from '../data/catalogue.ts';
import { shownBrain } from '../data/brain.ts';
import { activityIndex, defaultRun } from '../data/activity.ts';
import { leavesOut, scenarioById, viewCallouts } from '../scenarios/index.ts';
import { drawnOverlays, endedSlots, liveActive, mainSlot, packOf, slotsOf } from '../app/scene.ts';
import { isPatientComparison } from '../data/sims/comparison.ts';
import { presetCamera } from '../app/framing.ts';
import { layersFor, particleCount, type LayerCommand } from '../data/sims/layers.ts';
import { overlayCommand, type Overlay } from '../data/sims/overlay.ts';
import type { CellsHeader, SimFormat } from '../data/sims/types.ts';
import { minorFor, accepts, drawsLayer, FORMAT_SINCE, drawsSlot, placesSurfaces, brainMaps, placedIn, onBrainCortex } from './draws.ts';

const MAX_LABELS = 256;

// The draw rules moved to draws.ts; the bridge's importers still find them here.
export { parseApi, supports, minorFor, accepts, drawsLayer, drawsSims, FORMAT_SINCE, drawsFormat, drawsSlot, placesSurfaces, brainMaps, surfaceRun, placedIn, onBrainCortex } from './draws.ts';

/**
 * The HUD's note when a viewport slot isn't drawn: its viewport is too old or doesn't read the format,
 * a cell isn't placed in this pack, or the viewport refused the sim (`refused`, by path). A slot with
 * a drawn overlay (`overlays`) needs no note: the overlay stands in.
 */
export function simsNote(
  api: string | undefined,
  slots: SimSlot[],
  packSpace: string | undefined,
  extra: { sims?: string[]; refused?: Record<string, string>; overlays?: Overlay[]; maps?: readonly string[] } = {},
): string | null {
  const covered = (x: SimSlot) => !!extra.overlays?.some((o) => o.id.startsWith(`${x.slot}:`));
  const shown = slots.filter((x) => !x.appOnly && !covered(x));
  const old = shown.find((x) => !drawsSlot(api, extra.sims, x));
  if (old && extra.sims) return `This viewport doesn’t draw ${old.format} runs`;
  if (old) return `Viewport API ${api}: ${old.format === 'cells' ? 'cells' : 'sims'} draw from 0.${FORMAT_SINCE[old.format]}`;
  const refused = shown.find((x) => extra.refused?.[x.path] !== undefined);
  if (refused) return `${refused.label ?? 'This scene’s sim'} isn’t drawn in the 3D view`;
  const maps = placesSurfaces(extra.sims) ? extra.maps : undefined;
  const away = shown.find((x) => !placedIn(x.header, packSpace, maps));
  if (!away) return null;
  const placed = (away.header as CellsHeader).placement?.space;
  const what = away.format === 'cells' ? (away.header.kind === 'microcircuit' ? 'Circuit' : 'Cell') : `${away.label ?? 'This scene’s run'} is`;
  return placed ? `${what} placed in ${placed}, not ${packSpace}` : `${what} in ${away.header.space ?? 'its own space'}, not ${packSpace}`;
}

// The viewport's API, from its `ready` (registered at import, before any viewport is created).
let viewportApi: string | undefined;
on('ready', (e) => {
  viewportApi = e.api;
  useAtlas.getState().setEngine({ api: e.api, sims: e.sims, packs: e.packs, maps: e.maps, layers: e.layers });
});

/** The ids of the last `labels` command, in order: `labels` events report anchors in this order. */
export const trackedLabels: { ids: string[] } = { ids: [] };

/** Every system's mode: the scenario's choice, else the load default; a system the scene leaves out is hidden. */
export function layerModes(s: Pick<AtlasState, 'catalogue' | 'layers'> & Partial<Pick<AtlasState, 'scenario'>>): Record<string, Mode> {
  const out: Record<string, Mode> = {};
  const sc = s.scenario ? scenarioById(s.scenario) : undefined;
  for (const g of s.catalogue?.systems ?? []) out[g.system] = sc && leavesOut(sc, g.system) ? 'hidden' : (s.layers[g.system] ?? DEFAULT_MODES[g.system] ?? 'shown');
  return out;
}

function visibilityCmd(s: AtlasState): Command {
  return {
    cmd: 'visibility',
    set: [
      { target: { all: true }, mode: 'default' },
      ...Object.entries(layerModes(s)).map(([system, mode]) => ({ target: { system }, mode })),
      ...Object.entries(s.overrides).map(([id, mode]) => ({ target: { id }, mode: mode as Mode })),
    ],
  };
}

/** Label candidates: what's in focus first (selection, hover, the view's own callouts, matches), then everything shown. */
export function labelIds(s: AtlasState): string[] {
  const c = s.catalogue;
  if (!c) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (id: string | null | undefined) => {
    if (id && !seen.has(id) && out.length < MAX_LABELS) {
      seen.add(id);
      out.push(id);
    }
  };
  add(s.selected);
  add(s.hovered);
  for (const id of viewCallouts(s)) add(id);
  for (const id of s.matches ?? []) add(id);
  const sc = scenarioById(s.scenario);
  for (const st of c.drawable) if (!isContext(st) && !leavesOut(sc, st.system)) add(st.id);
  return out;
}

/**
 * The `sim` and `layer` commands for the scene's viewport slots, and its overlays, against what was
 * sent. Slots the viewport can't draw (`api` or its `sims` formats, the pack's space) are left out,
 * as if the scene had none; their overlays still go. A slot drawn on the shown brain's cortex through
 * a map sends no overlays: its `activity` layer lights the cortex and the deep structures itself. A
 * slot whose run has ended by the scene's time (`endedSlots`) keeps its sim, its layers off.
 */
export function simCommands(
  s: Pick<AtlasState, 'scenario' | 'sims' | 'overlays' | 'catalogue'> & Partial<Pick<AtlasState, 'brain' | 'live' | 'stroke' | 'load' | 'engine' | 'time' | 'toggles' | 'particles'>>,
  sent: { slots: Map<string, string>; overlays: Map<string, Overlay>; layers?: Map<string, string> },
  force = false,
  api?: string,
  sims?: string[],
): Command[] {
  const out: Command[] = [];
  const space = s.catalogue?.pack.space;
  const maps = brainMaps(s.load?.maps, s.brain?.maps, sims, s.engine?.maps);
  const want = new Map(slotsOf(s).filter((x) => !x.appOnly && drawsSlot(api, sims, x) && placedIn(x.header, space, maps)).map((x) => [x.slot, x]));
  const onCortex = new Set([...want.values()].filter((x) => onBrainCortex(x.header, space, maps)).map((x) => x.slot));
  const layers = (sent.layers ??= new Map());
  for (const slot of [...sent.slots.keys()])
    if (!want.has(slot)) {
      out.push({ cmd: 'sim', path: null, slot });
      sent.slots.delete(slot);
      layers.delete(slot);
    }
  // While the live solver drives the picture, the runs' vessels and particles go dark: they'd show
  // flow through a clot the run never had. Its overlay takes the place of theirs.
  const live = !!s.live && !!s.stroke && liveActive({ live: s.live, stroke: s.stroke });
  // Cortex activity off (the Activity section's switch): the runs' activity goes dark, layer and overlays.
  const dark = s.toggles?.activity === false;
  // The Vessels and Particles sections: vessels coloured by none keep the arteries' own colours; the
  // particles off, or fewer or more of them.
  const colourBy = s.stroke?.colourBy ?? 'velocity';
  const particles = s.particles ?? NO_PARTICLES;
  const ended = new Set(endedSlots(s));
  const darkened = (slot: string, l: LayerCommand, format: SimFormat): LayerCommand => {
    if (ended.has(slot) || (live && format === 'hemo' && (l.name === 'vessels' || l.name === 'particles')) || (dark && format === 'dynamics' && l.name === 'activity')) return { ...l, on: false };
    if (format === 'hemo' && l.name === 'vessels' && colourBy === 'none') return { ...l, on: false };
    if (format === 'hemo' && l.name === 'particles') return particles.on ? { ...l, count: particleCount(particles.density) } : { ...l, on: false };
    return l;
  };
  // A slot's layers go again whenever they change: the live solve or the Activity switch, the run's
  // end, the Vessels and Particles sections, or the overlay they take their quantity and range from
  // (a run's pressure overlay arrives after Colour by asks for it). Only those the viewport draws go.
  for (const [slot, x] of want) {
    const moved = force || sent.slots.get(slot) !== x.path;
    if (moved) out.push({ cmd: 'sim', path: x.path, slot });
    const ls = layersFor(slot, x.format, x.header, s.overlays.find((o) => o.id.startsWith(`${slot}:`)))
      .filter((l) => drawsLayer(s.engine?.layers, l.name))
      .map((l) => darkened(slot, l, x.format));
    const key = JSON.stringify(ls);
    if (moved || layers.get(slot) !== key) out.push(...ls);
    sent.slots.set(slot, x.path);
    layers.set(slot, key);
  }
  const activitySlots = new Set(slotsOf(s).filter((x) => x.format === 'dynamics').map((x) => x.slot));
  // The live solve's overlay replaces the runs' own; a slot drawn on the shown brain's cortex sends none.
  const slotOf = (o: Overlay) => o.id.slice(0, o.id.indexOf(':'));
  const overlays = live ? (s.live?.overlay ? [s.live.overlay] : []) : drawnOverlays(s).filter((o) => !onCortex.has(slotOf(o)) && !(dark && activitySlots.has(slotOf(o))));
  const wantOverlays = new Map(overlays.map((o) => [o.id, o]));
  for (const id of [...sent.overlays.keys()])
    if (!wantOverlays.has(id)) {
      out.push({ cmd: 'overlay', id, remove: true });
      sent.overlays.delete(id);
    }
  for (const [id, o] of wantOverlays) {
    if (!force && sent.overlays.get(id) === o) continue;
    out.push(overlayCommand(o));
    sent.overlays.set(id, o);
  }
  return out;
}

export type GlowCommand = Extract<Command, { cmd: 'glow'; at: unknown }>;

/**
 * The glows the view lays over the picture (viewport `glow`, 0.3): its preset's, on the scene's own pack,
 * while the scene's own model plays (dynamics, not a patient's fit, whose own onset zone is ringed) with
 * its activity on. Seizure's Onset view lights the hidden hippocampus this way, as design's screen does;
 * every other view sends none, and a glow is taken off when the view changes.
 */
export function glowsFor(s: Pick<AtlasState, 'scenario' | 'preset' | 'sims'> & Partial<Pick<AtlasState, 'viewPack' | 'toggles'>>): GlowCommand[] {
  const p = scenarioById(s.scenario).presets.find((x) => x.id === s.preset);
  const main = mainSlot(s);
  if (!p?.glow || s.viewPack || s.toggles?.activity === false || main?.format !== 'dynamics' || main.appOnly || isPatientComparison(main.header)) return [];
  return [{ cmd: 'glow', id: p.id, ...p.glow }];
}

export function connectBridge(vp: Viewport) {
  const store = useAtlas;
  let loading: Promise<void> | null = null;
  const skipped = new Set<string>();
  const send = (cmd: Command) => {
    if (!accepts(viewportApi, cmd)) {
      // Said once per command (per layer name), in the console; the HUD's engine status carries it on screen.
      const what = cmd.cmd === 'layer' ? `layer ${cmd.name}` : cmd.cmd;
      if (!skipped.has(what)) console.info(`[viewport] API ${viewportApi} viewport: not sending '${what}' (needs 0.${minorFor(cmd)})`);
      skipped.add(what);
      return;
    }
    if (loading) return;
    vp.send(cmd).catch((err: unknown) => {
      console.warn('[viewport]', cmd.cmd, err);
      // A refused sim draws nothing: its overlays stand in, or the HUD says so.
      if (cmd.cmd === 'sim' && cmd.path) refuse(cmd.path, reason(err));
    });
  };
  const reason = (err: unknown) => (err instanceof Error ? err.message : String((err as { error?: unknown } | null)?.error ?? err));
  const refuse = (path: string, why: string) => {
    const e = store.getState().engine;
    if (e.undrawn?.[path] !== why) store.getState().setEngine({ undrawn: { ...e.undrawn, [path]: why } });
  };
  const sentSims: Parameters<typeof simCommands>[1] = { slots: new Map(), overlays: new Map() };
  const syncSims = (force = false) => {
    if (loading) return;
    const s = store.getState();
    const cmds = simCommands(s, sentSims, force, viewportApi, s.engine.sims);
    for (const c of cmds) send(c);
    if (cmds.length) vp.setTime(s.time);
  };

  // The glows sent, by id, and how: one the view no longer wants is taken off (the engine keeps glows
  // across a `load`, so a scene change has to remove its own).
  const sentGlows = new Map<string, string>();
  const syncGlows = (force = false) => {
    if (loading) return;
    const want = new Map(glowsFor(store.getState()).map((g) => [g.id, g]));
    for (const id of [...sentGlows.keys()])
      if (!want.has(id)) {
        send({ cmd: 'glow', id, remove: true });
        sentGlows.delete(id);
      }
    for (const [id, g] of want) {
      const key = JSON.stringify(g);
      if (!force && sentGlows.get(id) === key) continue;
      send(g);
      sentGlows.set(id, key);
    }
  };

  const syncAll = () => {
    const s = store.getState();
    send(s.insets ? { cmd: 'insets', ...s.insets } : { cmd: 'insets', top: 0, right: 0, bottom: 0, left: 0 });
    send(visibilityCmd(s));
    send({ cmd: 'isolate', target: s.isolate });
    send({ cmd: 'select', id: s.selected });
    send({ cmd: 'emphasis', hover: s.hoverFrom === 'ui' ? s.hovered : null, matches: s.matches });
    send({ cmd: 'section', planes: s.section });
    send({ cmd: 'explode', amount: s.explode });
    trackedLabels.ids = s.labelsOn ? labelIds(s) : [];
    send({ cmd: 'labels', ids: trackedLabels.ids });
    if (s.presenting) send({ cmd: 'look', presentation: true });
    const sc = scenarioById(s.scenario);
    const preset = sc.presets.find((p) => p.id === s.preset);
    send({ cmd: 'camera', ...(preset ? presetCamera(s, preset, s.isolate ?? undefined) : { preset: 'hero' as const, ...(s.isolate ? { frame: s.isolate, direction: 'hero' as const } : {}) }), transitionMs: 0 });
    syncSims(true);
    syncGlows(true);
  };

  const load = async (pack: string) => {
    const s = store.getState();
    s.setLoad({ status: 'loading' });
    // A section's read of the last pack's volumes is over.
    store.setState({ reading: null });
    // A sim refused in one pack may draw in the next.
    s.setEngine({ undrawn: {} });
    const run = (async () => {
      // The brain the viewport draws over the pack comes with its catalogue, so the first sims sync
      // after the load knows which surface runs it places.
      const withBrain = loadCatalogue('/', pack).then(async (cat) => [cat, await shownBrain('/', pack, cat.pack)] as const);
      // A view's own pack (a connectome pack) plays the run it names as its default (`default_run`, H01's
      // first), asked for by name; one with no runs loads without.
      const named = pack === s.viewPack ? await activityIndex(`/packs/${pack}/`).then(defaultRun, () => null) : null;
      const [[cat, brain], reply] = await Promise.all([withBrain, vp.send({ cmd: 'load', pack, ...(named ? { run: named } : {}) })]);
      // A connectome pack replaces the anatomy, and the viewport unloads the sims over it itself (contract v0.3, part 1).
      if (reply.kind === 'connectome') {
        sentSims.slots.clear();
        sentSims.layers?.clear();
      }
      store.getState().setCatalogue(pack, cat, brain);
      return reply;
    })();
    loading = run.then(
      () => undefined,
      () => undefined,
    );
    try {
      // What the loaded brain places, when the viewport says (#541): the sims sync after this reads it. A
      // connectome pack's runs and the one it plays give the view its timeline.
      const { maps, runs, run: playing } = await run;
      loading = null;
      store.getState().setLoad({ status: 'ready', ...(maps ? { maps } : {}), ...(runs ? { runs, run: playing ?? null } : {}) });
      syncAll();
    } catch (err) {
      loading = null;
      const st = store.getState();
      // A view's own pack that won't load (a viewport without connectome packs): never a toast, a HUD
      // note, and the scene's own pack again.
      if (pack === st.viewPack) st.viewUnavailable(pack, reason(err));
      else st.setLoad({ status: 'error', error: reason(err) });
    }
  };

  store.subscribe(packOf, (pack) => void load(pack), { fireImmediately: true });
  store.subscribe((s) => s.insets, (i) => send({ cmd: 'insets', ...i }));
  store.subscribe((s) => glowsFor(s).map((g) => JSON.stringify(g)).join('\n'), () => syncGlows());
  store.subscribe((s) => [s.layers, s.overrides, s.catalogue] as const, () => send(visibilityCmd(store.getState())), { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] });
  store.subscribe((s) => s.isolate, (t) => send({ cmd: 'isolate', target: t }));
  store.subscribe((s) => s.selected, (id) => send({ cmd: 'select', id }));
  store.subscribe(
    (s) => (s.hoverFrom === 'ui' ? s.hovered : null),
    (hover) => send({ cmd: 'emphasis', hover }),
  );
  store.subscribe((s) => s.matches, (matches) => send({ cmd: 'emphasis', matches }));
  store.subscribe((s) => s.section, (planes) => send({ cmd: 'section', planes }));
  store.subscribe((s) => s.explode, (amount) => send({ cmd: 'explode', amount }));
  store.subscribe(
    (s) => (s.labelsOn ? labelIds(s).join('\n') : ''),
    (key) => {
      trackedLabels.ids = key ? key.split('\n') : [];
      send({ cmd: 'labels', ids: trackedLabels.ids });
    },
  );
  store.subscribe(
    (s) => s.cameraRequest,
    (req) => {
      if (!req) return;
      const { seq: _seq, ...rest } = req;
      send({ cmd: 'camera', ...rest });
    },
  );
  store.subscribe(
    (s) => [s.sims, s.overlays, s.catalogue, s.live, s.stroke, s.toggles.activity, s.particles] as const,
    () => syncSims(),
    { equalityFn: (a, b) => a.every((v, i) => v === b[i]) },
  );
  store.subscribe((s) => s.time, (t) => vp.setTime(t));
  // A run that ends before the scene goes dark past its end, and lights again before it.
  store.subscribe((s) => endedSlots(s).join(' '), () => syncSims());
  store.subscribe((s) => s.presenting, (presentation) => send({ cmd: 'look', presentation }));
}
